import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Flow, FlowFilters, FlowStatus, FlowNode, FlowEdge, PaginatedResult } from '../types/firestore';
import { createOrgScopedQuery, validateQueryResults } from '../lib/firestore-helpers';
import { retryOperation } from '../utils/error-handler';

const COLLECTION_NAME = 'flows';

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new flow with nested structure
 *
 * IMPORTANT: All flows use the canonical format:
 * { flow: { nodes, edges, entryPoint, exitPoints }, ... }
 */
export async function createFlow(
  organizationId: string,
  userId: string,
  data: {
    name: string;
    description?: string;
    nodes?: FlowNode[];
    edges?: FlowEdge[];
    tags?: string[];
    /** Carried over by duplicateFlow so a copy keeps its run-time inputs and limits. */
    inputs?: any[];
    outputs?: any[];
    config?: Record<string, any>;
  }
): Promise<Flow> {
  const nodes = data.nodes || [];
  const edges = data.edges || [];

  // Find entry/exit points from nodes
  const startNode = nodes.find(n => n.type === 'start');
  const endNodes = nodes.filter(n => n.type === 'end');

  const now = Timestamp.now();
  const flowData: Omit<Flow, 'id'> = {
    organizationId,
    name: data.name,
    ...(data.description ? { description: data.description } : {}),
    type: 'scenario',
    status: 'draft',
    // CANONICAL: Nested structure only. Firestore rejects undefined fields,
    // so omit entryPoint when there is no start node (empty-canvas create).
    flow: {
      nodes,
      edges,
      ...(startNode?.id ? { entryPoint: startNode.id } : {}),
      exitPoints: endNodes.map(n => n.id),
    },
    tags: data.tags ?? [],
    ...(data.inputs ? { inputs: data.inputs } : {}),
    ...(data.outputs ? { outputs: data.outputs } : {}),
    ...(data.config ? { config: data.config } : {}),
    createdBy: userId,
    createdAt: now,
    updatedAt: now,
    version: 1,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...flowData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    ...flowData,
    id: docRef.id,
  };
}

// ============================================================================
// READ
// ============================================================================

export async function getFlow(flowId: string, organizationId: string): Promise<Flow | null> {
  const docRef = doc(db, COLLECTION_NAME, flowId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const flow = { id: docSnap.id, ...docSnap.data() } as Flow;

  // System flows (starting with sys_) are accessible to all organizations
  const isSystemFlow = flowId.startsWith('sys_');

  // Verify tenant isolation (skip for system flows)
  if (!isSystemFlow && flow.organizationId !== organizationId) {
    throw new Error('Unauthorized access to flow');
  }

  return flow;
}

/**
 * Canonical set of system "quick test" flows. These have organizationId=null
 * (system) so they're excluded from org-scoped list queries (and Firestore
 * `list` rules), but read-by-id is permitted for system flows. Surfaced in the
 * /tests flow picker so quick tests can be scheduled like any other flow.
 * Keep in sync with quickTestService.getFlowIdForTestType.
 */
export const QUICK_TEST_FLOW_IDS = [
  'sys_quick_network_test_flow',
  'sys_quick_sms_test_flow',
  'sys_quick_data_test_flow',
  'sys_quick_data_performance_test_flow',
  'sys_quick_speedtest_test_flow',
  'sys_quick_iot_mqtt_test_flow',
  'sys_quick_iperf_test_flow',
  'sys_quick_voice_call_test_flow',
  'sys_quick_voice_agent_test_flow',
];

/**
 * Fetch the system quick-test flows by id (read-by-id is rule-permitted for
 * system flows; collection-list queries exclude them). Missing/unpublished
 * ones are skipped. Used to merge quick tests into the /tests flow picker.
 */
export async function getQuickTestFlows(organizationId: string): Promise<Flow[]> {
  const results = await Promise.all(
    QUICK_TEST_FLOW_IDS.map((id) => getFlow(id, organizationId).catch(() => null))
  );
  return results.filter((f): f is Flow => !!f && (f as any).status !== 'archived');
}

/**
 * Example flows every organization sees in the Flows page (seeded by
 * functions/src/seed/exampleFlowsSeed.ts — keep the ids in sync). System flows,
 * so fetched by id like the quick tests; Edit/Duplicate makes an org copy.
 */
export const EXAMPLE_FLOW_IDS = [
  'sys_example_scan_operators',
  'sys_example_ping_google_dns',
  'sys_example_manual_operator_selection',
  'sys_example_get_imsi',
  'sys_example_get_iccid',
];

export async function getExampleFlows(organizationId: string): Promise<Flow[]> {
  const results = await Promise.all(
    EXAMPLE_FLOW_IDS.map((id) => getFlow(id, organizationId).catch(() => null))
  );
  return results.filter((f): f is Flow => !!f && (f as any).status !== 'archived');
}

export async function getFlows(
  organizationId: string,
  filters?: FlowFilters
): Promise<Flow[]> {
  return retryOperation(async () => {
    let constraints: any[] = [orderBy('updatedAt', 'desc')];

    // Apply filters
    if (filters?.status && filters.status.length > 0) {
      constraints = [where('status', 'in', filters.status), orderBy('updatedAt', 'desc')];
    } else if (filters?.createdBy) {
      constraints = [where('createdBy', '==', filters.createdBy), orderBy('updatedAt', 'desc')];
    } else if (filters?.tags && filters.tags.length > 0) {
      constraints = [where('tags', 'array-contains-any', filters.tags), orderBy('updatedAt', 'desc')];
    }

    const q = createOrgScopedQuery(COLLECTION_NAME, organizationId, constraints);
    const snapshot = await getDocs(q);
    const flows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Flow));

    // Validate tenant separation
    validateQueryResults(flows, organizationId, COLLECTION_NAME);
    return flows;
  });
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a flow with nested structure
 *
 * IMPORTANT: All updates use the canonical format:
 * { flow: { nodes, edges, entryPoint, exitPoints }, ... }
 */
export async function updateFlow(
  flowId: string,
  organizationId: string,
  updates: Partial<Pick<Flow, 'name' | 'description' | 'flow' | 'tags' | 'status'>>
): Promise<void> {
  // First verify access
  const existingFlow = await getFlow(flowId, organizationId);
  if (!existingFlow) {
    throw new Error('Flow not found');
  }

  // If flow graph is being updated, recalculate entry/exit points
  let processedUpdates: any = { ...updates };
  if (updates.flow) {
    const nodes = updates.flow.nodes || [];
    const startNode = nodes.find(n => n.type === 'start');
    const endNodes = nodes.filter(n => n.type === 'end');
    processedUpdates.flow = {
      ...updates.flow,
      entryPoint: startNode?.id,
      exitPoints: endNodes.map(n => n.id),
    };
  }

  const docRef = doc(db, COLLECTION_NAME, flowId);
  await updateDoc(docRef, {
    ...processedUpdates,
    updatedAt: serverTimestamp(),
    version: existingFlow.version + 1,
  });
}

export async function updateFlowStatus(
  flowId: string,
  organizationId: string,
  status: FlowStatus
): Promise<void> {
  await updateFlow(flowId, organizationId, { status });
}

/**
 * A flow is "runnable" — and therefore shown in the Run / Schedule picker —
 * when it is neither a work-in-progress `draft` nor a retired `archived`.
 * `active` (legacy) and `published` (system flows + published tenant flows)
 * both count as runnable. Missing status is treated as runnable so a flow with
 * no status field is never silently hidden.
 */
export function isFlowRunnable(status?: FlowStatus | string): boolean {
  return status !== 'draft' && status !== 'archived';
}

export async function recordFlowExecution(
  flowId: string,
  organizationId: string
): Promise<void> {
  // First verify access
  const existingFlow = await getFlow(flowId, organizationId);
  if (!existingFlow) {
    throw new Error('Flow not found');
  }

  const docRef = doc(db, COLLECTION_NAME, flowId);
  await updateDoc(docRef, {
    lastExecutedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteFlow(flowId: string, organizationId: string): Promise<void> {
  // First verify access
  const existingFlow = await getFlow(flowId, organizationId);
  if (!existingFlow) {
    throw new Error('Flow not found');
  }

  const docRef = doc(db, COLLECTION_NAME, flowId);
  await deleteDoc(docRef);
}

// Soft delete (archive) instead of hard delete
export async function archiveFlow(flowId: string, organizationId: string): Promise<void> {
  await updateFlowStatus(flowId, organizationId, 'archived');
}

// ============================================================================
// DUPLICATE
// ============================================================================

export async function duplicateFlow(
  flowId: string,
  organizationId: string,
  userId: string,
  newName?: string
): Promise<Flow> {
  const existingFlow = await getFlow(flowId, organizationId);
  if (!existingFlow) {
    throw new Error('Flow not found');
  }

  // Use canonical nested structure
  return createFlow(organizationId, userId, {
    name: newName || `${existingFlow.name} (Copy)`,
    description: existingFlow.description,
    nodes: existingFlow.flow?.nodes || [],
    edges: existingFlow.flow?.edges || [],
    tags: existingFlow.tags,
    inputs: (existingFlow as any).inputs,
    outputs: (existingFlow as any).outputs,
    config: (existingFlow as any).config,
  });
}

// ============================================================================
// BATCH OPERATIONS
// ============================================================================

export async function batchUpdateFlowStatus(
  flowIds: string[],
  organizationId: string,
  status: FlowStatus
): Promise<void> {
  const batch = writeBatch(db);

  for (const flowId of flowIds) {
    const docRef = doc(db, COLLECTION_NAME, flowId);
    batch.update(docRef, {
      status,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

export async function batchDeleteFlows(
  flowIds: string[],
  organizationId: string
): Promise<void> {
  // Verify all flows belong to the organization first
  for (const flowId of flowIds) {
    const flow = await getFlow(flowId, organizationId);
    if (!flow) {
      throw new Error(`Flow ${flowId} not found or unauthorized`);
    }
  }

  const batch = writeBatch(db);

  for (const flowId of flowIds) {
    const docRef = doc(db, COLLECTION_NAME, flowId);
    batch.delete(docRef);
  }

  await batch.commit();
}

// ============================================================================
// EXPORT/IMPORT
// ============================================================================

export async function exportFlow(flowId: string, organizationId: string): Promise<string> {
  const flow = await getFlow(flowId, organizationId);
  if (!flow) {
    throw new Error('Flow not found');
  }

  // Remove organization-specific and system fields
  // Export uses canonical nested structure
  const exportData = {
    name: flow.name,
    description: flow.description,
    flow: flow.flow,
    tags: flow.tags,
    version: flow.version,
  };

  return JSON.stringify(exportData, null, 2);
}

export async function importFlow(
  organizationId: string,
  userId: string,
  flowJson: string
): Promise<Flow> {
  try {
    const flowData = JSON.parse(flowJson);

    // Support both new nested format and legacy top-level format
    const nodes = flowData.flow?.nodes || flowData.nodes || [];
    const edges = flowData.flow?.edges || flowData.edges || [];

    return createFlow(organizationId, userId, {
      name: flowData.name || 'Imported Flow',
      description: flowData.description,
      nodes,
      edges,
      tags: flowData.tags || [],
    });
  } catch (error) {
    throw new Error('Invalid flow JSON format');
  }
}

